import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ViewInvoiceModal from '../payment-management-center/components/ViewInvoiceModal';
import RefundModal from '../payment-management-center/components/RefundModal';

const PaymentHistoryManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [dateRange, setDateRange] = useState('last30days');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [amountRangeMin, setAmountRangeMin] = useState('');
  const [amountRangeMax, setAmountRangeMax] = useState('');
  const [viewInvoiceModal, setViewInvoiceModal] = useState({ isOpen: false, payment: null });
  const [refundModal, setRefundModal] = useState({ isOpen: false, payment: null });
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [payments, setPayments] = useState([
    {
      id: 1,
      transactionId: 'TXN-2026-001234',
      clientName: 'Acme Corp',
      customerEmail: 'contact@acmecorp.com',
      plan: 'Enterprise',
      amount: 14999,
      paymentDate: '2026-02-05 14:23:45',
      paymentMethod: 'Credit Card',
      status: 'Successful',
      invoiceId: 'INV-2026-001',
      gatewayResponse: 'Payment completed successfully',
      processingFee: 299,
      settlementDate: '2026-02-06',
      metadata: {
        cardLast4: '4242',
        cardBrand: 'Visa',
        gateway: 'Stripe'
      }
    },
    {
      id: 2,
      transactionId: 'TXN-2026-001235',
      clientName: 'Tech Solutions Ltd',
      customerEmail: 'admin@techsolutions.com',
      plan: 'Scale',
      amount: 5999,
      paymentDate: '2026-02-04 13:15:22',
      paymentMethod: 'PayPal',
      status: 'Successful',
      invoiceId: 'INV-2026-002',
      gatewayResponse: 'Transaction approved',
      processingFee: 119,
      settlementDate: '2026-02-05',
      metadata: {
        paypalEmail: 'billing@techsolutions.com',
        gateway: 'PayPal'
      }
    },
    {
      id: 3,
      transactionId: 'TXN-2026-001236',
      clientName: 'StartupXYZ',
      customerEmail: 'founder@startupxyz.com',
      plan: 'Growth',
      amount: 2999,
      paymentDate: '2026-02-03 12:45:10',
      paymentMethod: 'Credit Card',
      status: 'Failed',
      invoiceId: 'INV-2026-003',
      gatewayResponse: 'Insufficient funds',
      processingFee: 0,
      settlementDate: null,
      metadata: {
        cardLast4: '1234',
        cardBrand: 'Mastercard',
        gateway: 'Stripe',
        retryAttempts: 2
      }
    },
    {
      id: 4,
      transactionId: 'TXN-2026-001237',
      clientName: 'Digital Ventures',
      customerEmail: 'billing@digitalventures.com',
      plan: 'Growth',
      amount: 2999,
      paymentDate: '2026-02-02 11:30:05',
      paymentMethod: 'Bank Transfer',
      status: 'Pending',
      invoiceId: 'INV-2026-004',
      gatewayResponse: 'Awaiting bank confirmation',
      processingFee: 59,
      settlementDate: null,
      metadata: {
        bankName: 'HDFC Bank',
        gateway: 'Razorpay'
      }
    },
    {
      id: 5,
      transactionId: 'TXN-2026-001238',
      clientName: 'Fashion Forward',
      customerEmail: 'accounts@fashionforward.com',
      plan: 'Enterprise',
      amount: 14999,
      paymentDate: '2026-02-01 10:15:30',
      paymentMethod: 'Credit Card',
      status: 'Refunded',
      invoiceId: 'INV-2026-005',
      gatewayResponse: 'Refund processed',
      processingFee: 299,
      settlementDate: '2026-02-02',
      metadata: {
        cardLast4: '5678',
        cardBrand: 'Amex',
        gateway: 'Stripe',
        refundReason: 'Service not as expected'
      }
    }
  ]);

  // Move filteredPayments logic here, before it's used
  const filteredPayments = payments?.filter(payment => {
    const matchesSearch = payment?.clientName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         payment?.transactionId?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         payment?.customerEmail?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment?.status === statusFilter;
    const matchesPlan = planFilter === 'all' || payment?.plan === planFilter;
    const matchesPaymentMethod = paymentMethodFilter === 'all' || payment?.paymentMethod === paymentMethodFilter;
    
    const matchesAmountRange = (
      (amountRangeMin === '' || payment?.amount >= parseFloat(amountRangeMin)) &&
      (amountRangeMax === '' || payment?.amount <= parseFloat(amountRangeMax))
    );
    
    return matchesSearch && matchesStatus && matchesPlan && matchesPaymentMethod && matchesAmountRange;
  });

  const totalPages = Math.ceil(filteredPayments?.length / itemsPerPage);
  const paginatedPayments = filteredPayments?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleExportCSV = () => {
    const csvData = filteredPayments?.map(p => ({
      'Transaction ID': p?.transactionId,
      'Client Name': p?.clientName,
      'Email': p?.customerEmail,
      'Plan': p?.plan,
      'Amount': p?.amount,
      'Payment Method': p?.paymentMethod,
      'Status': p?.status,
      'Date': p?.paymentDate,
      'Invoice ID': p?.invoiceId
    }));
    console.log('Exporting CSV:', csvData);
  };

  const handleExportExcel = () => {
    console.log('Exporting Excel...');
  };

  const handleExportPDF = () => {
    console.log('Exporting PDF...');
  };

  const handleViewInvoice = (payment) => {
    setViewInvoiceModal({ isOpen: true, payment });
  };

  const handleRefund = (payment) => {
    setRefundModal({ isOpen: true, payment });
  };

  const toggleExpandRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getStatusBadge = (status) => {
    const config = {
      'Successful': { bg: 'bg-success/10', text: 'text-success', label: 'Successful', icon: 'CheckCircle' },
      'Failed': { bg: 'bg-destructive/10', text: 'text-destructive', label: 'Failed', icon: 'XCircle' },
      'Pending': { bg: 'bg-warning/10', text: 'text-warning', label: 'Pending', icon: 'Clock' },
      'Refunded': { bg: 'bg-info/10', text: 'text-info', label: 'Refunded', icon: 'RotateCcw' }
    };
    const statusConfig = config?.[status] || config?.['Successful'];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.bg} ${statusConfig?.text}`}>
        <Icon name={statusConfig?.icon} size={12} />
        {statusConfig?.label}
      </span>
    );
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 transition-smooth ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading flex items-center gap-3">
              <Icon name="History" size={32} className="text-primary" />
              Payment History Management
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body mt-2">
              Comprehensive transaction oversight with advanced filtering and detailed payment tracking
            </p>
          </div>

          {/* Advanced Filters */}
          <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Icon name="Filter" size={20} />
                Advanced Filters
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                  setPlanFilter('all');
                  setPaymentMethodFilter('all');
                  setAmountRangeMin('');
                  setAmountRangeMax('');
                  setDateRange('last30days');
                }}
              >
                Reset Filters
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search client, transaction, email..."
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="today">Today</option>
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
                <option value="last90days">Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="all">All Status</option>
                <option value="Successful">Successful</option>
                <option value="Failed">Failed</option>
                <option value="Pending">Pending</option>
                <option value="Refunded">Refunded</option>
              </select>
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="all">All Plans</option>
                <option value="Free">Free</option>
                <option value="Growth">Growth</option>
                <option value="Scale">Scale</option>
                <option value="Enterprise">Enterprise</option>
              </select>
              <select
                value={paymentMethodFilter}
                onChange={(e) => setPaymentMethodFilter(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="all">All Payment Methods</option>
                <option value="Credit Card">Credit Card</option>
                <option value="PayPal">PayPal</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </select>
              <input
                type="number"
                value={amountRangeMin}
                onChange={(e) => setAmountRangeMin(e?.target?.value)}
                placeholder="Min Amount"
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <input
                type="number"
                value={amountRangeMax}
                onChange={(e) => setAmountRangeMax(e?.target?.value)}
                placeholder="Max Amount"
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                iconName="Download"
                iconPosition="left"
              >
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                iconName="FileSpreadsheet"
                iconPosition="left"
              >
                Export Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                iconName="FileText"
                iconPosition="left"
              >
                Export PDF
              </Button>
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Transaction ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Client Info</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPayments?.map((payment) => (
                  <React.Fragment key={payment?.id}>
                    <tr className="border-b border-border hover:bg-muted/30 transition-smooth">
                      <td className="px-4 py-4 font-mono text-sm text-foreground">{payment?.transactionId}</td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-foreground">{payment?.clientName}</p>
                          <p className="text-xs text-muted-foreground">{payment?.customerEmail}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-foreground">{payment?.plan}</td>
                      <td className="px-4 py-4 text-foreground font-semibold">₹{payment?.amount?.toLocaleString()}</td>
                      <td className="px-4 py-4 text-foreground">{payment?.paymentMethod}</td>
                      <td className="px-4 py-4 text-foreground text-sm">{new Date(payment?.paymentDate)?.toLocaleString()}</td>
                      <td className="px-4 py-4">{getStatusBadge(payment?.status)}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleExpandRow(payment?.id)}
                            className="p-2 hover:bg-muted rounded-md transition-smooth"
                            title="View Details"
                          >
                            <Icon name={expandedRow === payment?.id ? "ChevronUp" : "ChevronDown"} size={16} className="text-primary" />
                          </button>
                          <button
                            onClick={() => handleViewInvoice(payment)}
                            className="p-2 hover:bg-muted rounded-md transition-smooth"
                            title="View Invoice"
                          >
                            <Icon name="Eye" size={16} className="text-primary" />
                          </button>
                          {payment?.status === 'Successful' && (
                            <button
                              onClick={() => handleRefund(payment)}
                              className="p-2 hover:bg-destructive/10 rounded-md transition-smooth"
                              title="Refund"
                            >
                              <Icon name="RotateCcw" size={16} className="text-destructive" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedRow === payment?.id && (
                      <tr className="bg-muted/20 border-b border-border">
                        <td colSpan="8" className="px-4 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Invoice ID</p>
                              <p className="text-sm font-mono text-foreground">{payment?.invoiceId}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Gateway Response</p>
                              <p className="text-sm text-foreground">{payment?.gatewayResponse}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Processing Fee</p>
                              <p className="text-sm text-foreground">₹{payment?.processingFee?.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Settlement Date</p>
                              <p className="text-sm text-foreground">{payment?.settlementDate || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Gateway</p>
                              <p className="text-sm text-foreground">{payment?.metadata?.gateway}</p>
                            </div>
                            {payment?.metadata?.cardLast4 && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Card Details</p>
                                <p className="text-sm text-foreground">{payment?.metadata?.cardBrand} •••• {payment?.metadata?.cardLast4}</p>
                              </div>
                            )}
                            {payment?.metadata?.retryAttempts && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Retry Attempts</p>
                                <p className="text-sm text-foreground">{payment?.metadata?.retryAttempts}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4 mb-6">
            {paginatedPayments?.map((payment) => (
              <div key={payment?.id} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono text-sm font-semibold text-foreground">{payment?.transactionId}</p>
                    <p className="text-sm font-medium text-foreground mt-1">{payment?.clientName}</p>
                    <p className="text-xs text-muted-foreground">{payment?.customerEmail}</p>
                  </div>
                  {getStatusBadge(payment?.status)}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Plan</p>
                    <p className="text-sm font-medium text-foreground">{payment?.plan}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Amount</p>
                    <p className="text-sm font-semibold text-foreground">₹{payment?.amount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Method</p>
                    <p className="text-sm font-medium text-foreground">{payment?.paymentMethod}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Date</p>
                    <p className="text-sm font-medium text-foreground">{new Date(payment?.paymentDate)?.toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewInvoice(payment)}
                    iconName="Eye"
                    iconPosition="left"
                    fullWidth
                  >
                    View
                  </Button>
                  {payment?.status === 'Successful' && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRefund(payment)}
                      iconName="RotateCcw"
                      iconPosition="left"
                      fullWidth
                    >
                      Refund
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredPayments?.length)} of {filteredPayments?.length} payments
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  iconName="ChevronLeft"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  iconName="ChevronRight"
                  iconPosition="right"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Modals */}
      <ViewInvoiceModal
        isOpen={viewInvoiceModal?.isOpen}
        payment={viewInvoiceModal?.payment}
        onClose={() => setViewInvoiceModal({ isOpen: false, payment: null })}
      />
      <RefundModal
        isOpen={refundModal?.isOpen}
        payment={refundModal?.payment}
        onClose={() => setRefundModal({ isOpen: false, payment: null })}
      />
    </div>
  );
};

export default PaymentHistoryManagement;